<template>
  <el-container
    class="tillhub-page-wrapper overflow-hidden text-th-text-primary bg-th-background-color"
    direction="vertical"
  >
    <main
      class="h-full flex flex-col overflow-auto"
      :class="{
        'p-6': !removeContentPadding
      }"
    >
      <header
        v-if="showHeader"
        class="mb-6"
        :class="{
          'px-6 pt-6': removeContentPadding
        }"
      >
        <nav v-if="showBreadcrumbs" data-testid="breadcrumbs">
          <ol class="flex items-center flex-wrap text-sm">
            <li v-for="(breadcrumb, index) in trail" :key="index">
              <span
                v-if="breadcrumb === trail.at(-1)"
                class="text-th-text-regular"
                aria-current="page"
              >
                <template v-if="breadcrumb.label">
                  {{ breadcrumb.label }}
                </template>

                <span v-else> ... </span>
              </span>

              <router-link
                v-else
                :to="breadcrumb.route || '#'"
                class="text-th-text-primary hover:text-th-primary transition duration-100"
              >
                <template v-if="breadcrumb.label">
                  {{ breadcrumb.label }}
                </template>

                <span v-else> ... </span>
              </router-link>

              <span
                v-if="breadcrumb !== trail.at(-1)"
                class="font-bold mx-2 text-th-text-placeholder"
                aria-hidden="true"
                v-text="'/'"
              />
            </li>
          </ol>
        </nav>

        <div class="flex justify-between items-center gap-2 flex-wrap">
          <div
            v-if="showTitle"
            class="flex items-center min-h-8 gap-x-3 gap-y-1 flex-wrap flex-1 max-w-full"
          >
            <div class="flex items-center max-w-full">
              <router-link
                v-if="showBackButton && backButtonRoute && pageTitle"
                :to="backButtonRoute"
                class="w-5 h-5 text-th-text-regular mr-4"
              >
                <back-icon class="w-5 align-top" />
              </router-link>

              <h2
                v-if="pageTitle"
                class="text-xl leading-7 m-0 truncate min-w-[0px]"
                :title="pageTitle"
                data-testid="page-title"
              >
                {{ pageTitle }}
              </h2>

              <el-skeleton v-else :rows="0" class="leading-[0]" animated />
            </div>

            <div v-if="$slots['title-addon']">
              <slot name="title-addon" />
            </div>
          </div>

          <div
            id="page-wrapper-header-actions"
            class="header-actions flex items-center gap-3 shrink-0"
          >
            <slot name="header-actions" />
          </div>
        </div>

        <div v-if="navigation.length || $slots.navigation" class="mt-2">
          <slot name="navigation">
            <secondary-navigation :items="navigation" />
          </slot>
        </div>
      </header>

      <slot />
    </main>

    <footer
      v-if="actions || $slots.actions"
      class="shadow-[0_0_6px] shadow-black/[0.11] px-6 py-4 h-auto bg-white z-[1]"
    >
      <slot name="actions">
        <actions v-bind="{ ...$attrs, ...actions }" />
      </slot>
    </footer>
  </el-container>
</template>

<script>
import Actions from '@shared/components/actions'
import { BackIcon } from '@shared/assets/icons'
import { useBreadcrumbsProvider } from '@shared/composables/use-breadcrumbs'
import { computed } from 'vue'
import SecondaryNavigation from '@shared/components/secondary-navigation/index'
import { useRouteQueryHistory } from '@shared/composables/use-route-query-history'

export default {
  name: 'ThPageWrapper',
  components: {
    Actions,
    BackIcon,
    SecondaryNavigation
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    actions: {
      type: Object,
      default: undefined
    },
    removeContentPadding: {
      type: Boolean,
      default: false
    },
    backRoute: {
      type: [String, Object],
      default: undefined
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    showBreadcrumbs: {
      type: Boolean,
      default: true
    },
    /**
     * @type {{label: string; route: string; query?: string}[]}
     */
    navigation: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const { trail } = useBreadcrumbsProvider()

    const { applyQueryToRoute } = useRouteQueryHistory()

    const pageTitle = computed(() => {
      return props.title || trail.value.at(-1)?.label
    })

    const backButtonRoute = computed(() => {
      if (props.backRoute) {
        return props.backRoute
      }

      const lastBreadcrumbRoute = trail.value
        .slice()
        .reverse()
        .find(({ route }) => !!route)?.route

      if (!lastBreadcrumbRoute) {
        return null
      }

      return applyQueryToRoute(lastBreadcrumbRoute)
    })

    return {
      backButtonRoute,
      trail,
      pageTitle
    }
  }
}
</script>

<style scoped>
.tillhub-page-wrapper {
  height: 100%;
  width: 100%;
}
.tillhub-page-wrapper > * {
  margin: 0;
  padding: 0;
}

.header-actions :deep(.el-button) {
  margin-left: 0;
}
</style>
